import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  AfterViewChecked,
  AfterViewInit,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog, MatDialogConfig, DialogPosition } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { AuthAppService } from 'avn/services/auth-app/auth-app.service';
import { BusinessMainService } from 'avn/services/business-main.service';
import { Constants } from 'avn/services/constants';
import { DomHelperService } from 'avn/services/dom-helper.service';
import { FiltersComponent } from 'avn/shared/components/filters/filters.component';
import { SearchBoxPopComponent } from 'avn/shared/components/search-box/search-box-pop/search-box-pop.component';
import { SchemaService } from 'avn/shared/components/seo-extention/schema.service';
import { SortComponent } from 'avn/shared/components/sort/sort.component';
import { SlugTextPipe } from 'avn/shared/pipes/slug-text.pipe';
import { ConfirmPopData, SharedCompsService } from 'avn/shared/shared-comps.service';
import * as moment from 'moment';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { Location as Loc } from '../../../interfaces/homepage';
import { ApiService } from '../../../services/api.service';
import { StoreService } from '../../../services/store.service';

import { ExitIntentComponent } from './exit-intent/exit-intent.component';
import { ExitUserDetailsComponent } from './exit-user-details/exit-user-details.component';
import { Location, SearchRequest } from './service.interface';

// STATES
// Define keys for transfer state
const LANGUAGES_STATE_KEY = makeStateKey<any[]>('languages');
// const SEARCH_PROVIDERS_STATE_KEY = makeStateKey<any[]>('searchProvidersState');

@Component({
  selector: 'app-search-page',
  templateUrl: './search-page.component.html',
  styleUrls: ['./search-page.component.scss'],
  providers: [DomHelperService]
})
export class SearchPageComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  private readonly backNavigationGuardStateKey = '__avaanaBackGuard';

  constructor(
    private route: ActivatedRoute,
    private api: ApiService,
    private store: StoreService,
    private dialog: MatDialog,
    private schemaService: SchemaService,
    private router: Router,
    private fb: FormBuilder,
    private businessMainService: BusinessMainService,
    @Inject(DOCUMENT) private dom: any,
    @Inject(PLATFORM_ID) private platformId: any,
    private slugText: SlugTextPipe,
    private cd: ChangeDetectorRef,
    private domHelper: DomHelperService,
    private transferState: TransferState, // Inject TransferState,
    private shared: SharedCompsService,
    private auth0: AuthService,
    private authapp: AuthAppService
  ) {
    this.isServer = isPlatformServer(platformId); // Detect if on the server
  }

  isServer = false;

  pageSize = 50;
  browserTest: any;

  currentView = 'list';

  routeType = 'service';
  groupName = 'Physical';
  isSymptom = false;

  service = '';
  symptom = '';
  suburb = '';
  postCode = '';
  selectedDate: string = moment().format('YYYY-MM-DD');

  currentTab: 'In Person' | 'Online';

  practitionerList: any[] = [];

  selectedDistance = 10;

  pageNumber = 1;
  totalPages = 1;
  totalItems = 0;
  meraLocation = new Loc();

  fetchingData = false;

  subscriptions: any = [];
  isMobile = false;
  claimNumber: any = '';

  isCitySuburb = false;
  allHealthConditions = Constants.HEALTH_CONDITIONS;
  languages: any = [];

  professionTitle = '';

  loadingLocations = false;

  centerCoords: [number, number];

  fetchingmoreData = false;
  private tabSetManually = false;
  private exitIntentDialogOpen = false;
  private claimLinkDialogOpen = false;
  private claimLinkHandled = false;

  // Stepper state
  showQuestionnaire = false;
  currentStep = 0;
  maxStep = 2;

  // Preferences model
  preferenceForm = this.fb.group({
    gender: [''],
    distance_from_me: [7],
    timings: [''],
    amenities: ['']
  });
  location: string;
  sliderBackground = this.buildSliderGradient(this.getSliderPercent(7));

  checkUserLogin: any;

  // Toggle questionnaire visibility
  toggleQuestionnaire(): void {
    this.showQuestionnaire = !this.showQuestionnaire;
  }

  expanded: boolean[] = [true];
  isPractDistanceOver20km = false;
  toggleAccordion(index: number) {
    this.expanded[index] = !this.expanded[index];
  }

  @ViewChild('availabilityTrigger') availabilityMenuTrigger!: MatMenuTrigger;
  @ViewChild('distanceTrigger') distanceMenuTrigger!: MatMenuTrigger;
  @ViewChild('sortTrigger') sortTrigger!: MatMenuTrigger;
  @ViewChild('availabilityFilterBox') availabilityFilterBox?: ElementRef<HTMLElement>;
  @ViewChild('distanceFilterBox') distanceFilterBox?: ElementRef<HTMLElement>;
  @ViewChild('sortFilterBox') sortFilterBox?: ElementRef<HTMLElement>;

  @ViewChild('slider', { static: false }) slider!: ElementRef<HTMLInputElement>;
  private sliderInitialized = false;

  ngAfterViewInit(): void {
    const initialValue = this.preferenceForm.get('distance_from_me')?.value ?? 7;
    this.setSliderProgress(initialValue);
  }
  ngAfterViewChecked() {
    // Only run when step 1 is active and element is available
    if (this.currentStep === 1 && this.slider) {
      if (!this.sliderInitialized) {
        const value = this.preferenceForm.get('distance_from_me')?.value ?? 7;
        this.setSliderProgress(value);
        this.sliderInitialized = true;
      }
    } else if (this.sliderInitialized) {
      this.sliderInitialized = false;
    }
  }

  updateSliderGradient(event: Event) {
    const input = event.target as HTMLInputElement;
    this.setSliderProgress(+input.value);
  }

  setSliderProgress(value: any) {
    const percent = this.getSliderPercent(Number(value));
    this.slider?.nativeElement?.style.setProperty('--progress', `${percent}%`);
    this.sliderBackground = this.buildSliderGradient(percent);
  }

  private getSliderPercent(value: number) {
    const min = 1;
    const max = 50;
    const numeric = Number.isFinite(value) ? value : min;
    const percent = ((numeric - min) / (max - min)) * 100;
    return Math.max(0, Math.min(100, percent));
  }

  private buildSliderGradient(percent: number) {
    return `linear-gradient(90deg, #00c26e ${percent}%, #e2e8f0 ${percent}%)`;
  }

  ngOnInit(): void {
    // Retrieving the EML_JWT_KEY value to verify whether the user exists.
    this.checkUserLogin = localStorage.getItem('EML_JWT_KEY');

    this.seedBackNavigationState();
    this.domHelper.loadStyle('https://cdnjs.cloudflare.com/ajax/libs/mapbox-gl/3.8.0/mapbox-gl.min.css');
    this.domHelper.loadScript('https://cdnjs.cloudflare.com/ajax/libs/mapbox-gl/3.8.0/mapbox-gl.min.js');
    // this.domHelper.loadScript('https://cdnjs.cloudflare.com/ajax/libs/mapbox-gl/3.8.0/mapbox-gl-csp-worker.min.js');

    const sub1 = this.route.data.subscribe((data) => {
      this.routeType = data['url_type'];
    });
    // this.fetchParams();
    this.subscriptions.push(sub1);
    if (this.dom.documentElement.clientWidth < 1100) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
    if (this.isServer) {
      this.getlanguages();
    } else {
      // On the client, retrieve the state
      const storedLanguages = this.transferState.get(LANGUAGES_STATE_KEY, null);
      if (storedLanguages) {
        this.languages = storedLanguages;
      } else {
        this.getlanguages();
      }
    }
    if (this.queryParamSub) {
      this.queryParamSub.unsubscribe();
    }
    this.queryParamSub = this.route.queryParams.subscribe((queryParams: any) => {
      const cond =
        (Object.keys(queryParams).length !== 0 || Object.keys(this.filtersApplied).length !== 0) &&
        JSON.stringify(queryParams) !== JSON.stringify(this.filtersApplied);
      this.filtersApplied = queryParams;
      this.preferenceForm.patchValue({
        gender: queryParams['gender'] || '',
        distance_from_me: queryParams['distance_from_me'] || 7,
        timings: queryParams['timings'] || '',
        amenities: queryParams['amenities'] || ''
      });
      if (!this.firstTimeQueryParams && cond) {
        this.applyFilters();
        return;
      }
      if (queryParams?.['dt']) {
        this.selectedDate = queryParams?.['dt'];
      }
      if (queryParams?.['claim_number']) {
        this.claimNumber = queryParams?.['claim_number'];
        localStorage.setItem('claim_number', this.claimNumber);
      }
      this.fetchParams();
      this.firstTimeQueryParams = false;
    });
    this.subscriptions.push(this.queryParamSub);

    const currentPageTypeSub = this.store.currentPageType.subscribe((current_view: string) => {
      this.currentView = current_view;
    });
    this.subscriptions.push(currentPageTypeSub);

    const distanceSub = this.preferenceForm.get('distance_from_me')?.valueChanges.subscribe((val) => {
      this.setSliderProgress(val);
    });
    this.subscriptions.push(distanceSub);
  }

  additionalNeeds(e: any) {
    this.preferenceForm.get('amenities')?.setValue(e);
  }

  changeCurrentView() {
    this.currentView = this.currentView === 'list' ? 'map' : 'list';
    this.store.currentPageType.next(this.currentView);
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent) {
    if ((event.state || {})[this.backNavigationGuardStateKey]) {
      return;
    }

    this.keepCurrentPageInView();

    // Added a condition so that the ExitIntentComponent is shown only to new users and not to logged-in users.
    if (!this.checkUserLogin && !this.exitIntentDialogOpen) {
      this.exitIntentDialogOpen = true;
      const dialogRef = this.dialog.open(ExitIntentComponent, {
        width: '600px',
        disableClose: true,
        height: 'auto',
        maxHeight: '90vh',
        panelClass: 'roundedPanel'
      });
      dialogRef.afterClosed().subscribe((result) => {
        console.info('here', result);
        if (result) {
          const dialogRef1 = this.dialog.open(ExitUserDetailsComponent, {
            width: '600px',
            disableClose: true,
            height: 'auto',
            maxHeight: '90vh',
            panelClass: 'roundedPanel',
            data: { ...result }
          });
          dialogRef1.afterClosed().subscribe(() => {
            console.info('here');
            this.exitIntentDialogOpen = false;
          });
          return;
        }

        this.exitIntentDialogOpen = false;
      });
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if ((event.target as Window)?.innerWidth < 1100) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
  }

  checkIsSymtop() {
    return this.allHealthConditions.indexOf(this.service) > -1;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: any) => {
      try {
        sub.unsubscribe();
      } catch (error) {
        console.info(error);
      }
    });
  }

  private seedBackNavigationState() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (history.state?.[this.backNavigationGuardStateKey]) {
      return;
    }

    const currentState = history.state && typeof history.state === 'object' ? history.state : {};
    history.pushState({ ...currentState, [this.backNavigationGuardStateKey]: true }, '', location.href);
  }

  private keepCurrentPageInView() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    history.forward();
  }

  getlanguages() {
    const responseObs = this.businessMainService.getLanguages();
    const languagesSub = responseObs.subscribe({
      next: (response) => {
        this.languages = this.generateLanguageOptions(response.data);
        // Store the state for SSR
        if (this.isServer) {
          this.transferState.set(LANGUAGES_STATE_KEY, this.languages);
        }
      },
      error: (error) => {
        console.info(error);
      }
    });
    this.subscriptions.push(languagesSub);
  }

  isSubCategoryPage = false;
  mainCategory = '';
  subCatObj = Constants.SUBCATEGORY_LIST;

  checkIsSubCategoryPage(category: string) {
    this.subCatObj = Constants.SUBCATEGORY_LIST;
    for (const key in this.subCatObj) {
      if (this.subCatObj[key].findIndex((x: any) => x.key === category) > -1) {
        this.mainCategory = key;
        return true;
      }
    }
    return false;
  }

  paramsSub: any;
  queryParamSub: any;
  firstTimeQueryParams = true;

  fetchParams() {
    if (this.paramsSub) {
      this.paramsSub.unsubscribe();
    }

    this.paramsSub = this.route.params
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(async (params_data) => {
        const params: any = { ...params_data };
        this.practitionerList = [];
        this.isSubCategoryPage = this.checkIsSubCategoryPage(params['location']);
        if (this.isSubCategoryPage) {
          params.parent_category = params.service;
          params.service = params.location;
          params.location = params.postCode;
          params.postCode = params.date;
        }

        this.service = params['service'] || '';
        localStorage.setItem('service', this.service);
        this.generateGroupName(this.service);

        if (this.routeType === 'symptom') {
          this.service = '';
          this.symptom = params['service'];
          this.suburb = params['location'] || this.store.meraLocation.city_name || '';
          this.postCode = params['postCode'] || this.store.meraLocation.post_code || '';
        } else {
          this.suburb = params['location'] || this.store.meraLocation.city_name || '';
          this.postCode = params['postCode'] || this.store.meraLocation.post_code || '';
        }

        if (this.checkIsSymtop()) {
          this.routeType = 'symptom';
          this.symptom = this.service;
          this.service = '';
        }

        if (!this.tabSetManually) {
          if (this.service.toLowerCase() === 'physiotherapy') {
            this.currentTab = 'In Person';
          } else if (this.service.toLowerCase() === 'psychology') {
            this.currentTab = 'Online';
          }
        }

        params['date'] = this.route.snapshot.queryParams['dt'] || '';
        this.selectedDate = moment(params['date'] || moment()).format('YYYY-MM-DD');
        this.orderbyApplied = this.service.toLowerCase() === 'physiotherapy' ? 'distance' : 'earliest_availability';
        try {
          // Wait for the location to be set
          await this.intialLocation(params);
          this.applyFilters(); // Now the payload will have lat & lng values
          this.updateLocation();
          this.store.searchBoxParams.next(params);
        } catch (err) {
          console.error('Failed to set initial location:', err);
        }
      });
    this.subscriptions.push(this.paramsSub);
  }

  intialLocation(params: any): Promise<void> {
    return new Promise((resolve, reject) => {
      if (
        params &&
        (this.store.meraLocation.lat !== '' || this.store.meraLocation.lat === null) &&
        (this.store.meraLocation.lng !== '' || this.store.meraLocation.lng === null)
      ) {
        const location: any = {};
        location.post_code = params.postCode;
        location.city_name = this.slugText.transform(params.location, 'capitalize');
        location.state_name = this.getStateForSuburbByPostCode(this.postCode);

        const geoSub = this.api
          .getGeoCodingByAddress(`${location.city_name}, ${location.state_name} ${location.post_code}`)
          .subscribe({
            next: (response: any) => {
              const coords = response.geocode_details.results[0]?.geometry?.location || {};
              location.lat = coords.lat;
              location.lng = coords.lng;

              this.store.setmeraLocation(location);
              resolve(); // Indicate that the location has been set
            },
            error: (err) => {
              console.error('Error fetching geocoding:', err);
              reject(err);
            }
          });
        this.subscriptions.push(geoSub);
      } else {
        resolve(); // No need to fetch, resolve immediately
      }
    });
  }

  updateLocation() {
    if (
      this.store.meraLocation.post_code !== this.postCode ||
      this.store.meraLocation.city_name?.toLowerCase()?.replace(/-/g, ' ') !==
        this.suburb?.toLowerCase()?.replace(/-/g, ' ')
    ) {
      const location: any = {};
      location.lat = '';
      location.lng = '';
      location.post_code = this.postCode;
      location.city_name = this.slugText.transform(this.suburb, 'capitalize');
      location.state_name = this.getStateForSuburbByPostCode(this.postCode);
      this.store.setmeraLocation(location);
      this.getCoordinates(location);
    }
  }

  getCoordinates(location: any) {
    const geoSub = this.api.getGeoCodingByAddress(`${location.city_name}, ${location.state_name} ${location.post_code}`).subscribe({
      next: (response: any) => {
        const coords = response.geocode_details.results[0].geometry.location;
        const loc = this.store.getmeraLocation();
        loc.lat = coords.lat;
        loc.lng = coords.lng;
        this.store.setmeraLocation(location);
        this.centerCoords = [coords.lng, coords.lat];
        this.loadingLocations = false;
      }
    });
    this.subscriptions.push(geoSub);
  }

  getStateForSuburbByPostCode(post_code: string): string {
    let state = '';
    if (post_code.indexOf('2') === 0) {
      state = 'New South Wales';
    } else if (post_code.indexOf('3') === 0) {
      state = 'Victoria';
    } else if (post_code.indexOf('4') === 0) {
      state = 'Queensland';
    } else if (post_code.indexOf('5') === 0) {
      state = 'South Australia';
    } else if (post_code.indexOf('6') === 0) {
      state = 'Western Australia';
    }
    return state;
  }

  generateGroupName(cat: string) {
    const groups: any = Constants.CATEGROY_GROUPS;
    for (const key in groups) {
      const index = groups[key].findIndex((x: any) => x.slug === cat);
      if (index > -1) {
        this.groupName = key.toLowerCase();
        break;
      }
    }
  }

  applyFilters() {
    for (const key in this.filtersApplied) {
      const index = this.filterdata.findIndex((x: any) => x.function === key);
      if (index > -1) {
        if (this.filterdata[index].type === 'checkbox') {
          this.filterdata[index].value = this.filtersApplied[key]?.split(',');
        } else {
          this.filterdata[index].value = this.filtersApplied[key];
        }
      }
    }
    if (this.filtersApplied.distance_from_me) {
      this.selectedDistance = Number(this.filtersApplied.distance_from_me);
    }
    const url = this.router.url.split('?');
    this.filterParams = url[1] || '';
    this.fetchListData(false, true);
  }

  fetchListData(get_all = false, applyfilters = false, searchRequest?: SearchRequest) {
    if (this.pracApiSub) {
      this.pracApiSub.unsubscribe();
    }
    this.fetchingData = true;
    this.nodata = false;
    if (!searchRequest) {
      searchRequest = new SearchRequest();
      searchRequest.cal_date = this.selectedDate;
      searchRequest.service = this.service === 'health-and-wellbeing-services' ? 'all' : this.service;
      searchRequest.symptom = this.symptom;
      searchRequest.online = this.currentTab === 'Online';
      searchRequest.locObj = new Location();
      searchRequest.locObj.city = this.suburb;
      searchRequest.locObj.post_code = this.postCode;
      searchRequest.locObj.state = this.getStateForSuburbByPostCode(this.postCode);
      searchRequest.locObj.Loclat =
        this.store.meraLocation?.city_name?.toLowerCase() === this.suburb.toLowerCase()
          ? this.store.meraLocation.lat
          : null;
      searchRequest.locObj.Loclng =
        this.store.meraLocation?.city_name?.toLowerCase() === this.suburb.toLowerCase()
          ? this.store.meraLocation.lng
          : null;
      searchRequest.page_size = this.pageSize;
      searchRequest.distance_range = this.selectedDistance;
    }
    this.hiddenCount = 0;
    this.fetchPracList(searchRequest, get_all, applyfilters);
  }

  nodata = false;
  pracApiSub: any;
  fetchPracList(params: SearchRequest, get_all = false, filters = false) {
    this.loadingLocations = true;
    this.pracApiSub = this.api
      .getPractitionersList(
        params,
        this.pageNumber,
        this.currentTab === 'Online',
        this.filterParams,
        this.orderbyApplied
      )
      .subscribe({
        next: (response: any) => {
          this.setPracsAfterResponse(params, get_all, filters, response);
        },
        error: (err: any) => {
          this.nodata = this.practitionerList.length === 0;
          this.fetchingData = false;
          this.totalPages = err.total_pages;
          this.totalItems = err.total_practitioners;
          this.practitionerList = [];
          // if (err.status === 404) {
          //   this.router.navigate(['/notfound'], {
          //     skipLocationChange: true
          //   });
          // }
          if (this.handleCompletedClaimLinkError(err)) {
            return;
          }
          const isLoggedIn = !!this.authapp.getSavedUser()?.email;
          const options = new ConfirmPopData();
          options.okClassName = 'btn-warning';
          if (err.error.message === 'No Practitioners Available') {
            options.okButtonText = 'Ok';
            options.title = 'No Practitioners';
            options.message = err?.error?.message;
          } else {
            options.okButtonText = isLoggedIn ? 'Bookings' : 'Login';
            options.title = 'Link Already Used';
            options.message =
              err?.error?.message ||
              "It appears you've already made a booking using this link. To view or manage your existing booking, please log in to your account.";
          }
          const dialogRef = this.shared.confirmPop(options);
          dialogRef.afterClosed().subscribe({
            next: (result: any) => {
              if (result && options.okButtonText !== 'Ok') {
                if (isLoggedIn) {
                  this.router.navigate(['/profile/booking']);
                } else {
                  this.auth0.logout({
                    logoutParams: {
                      returnTo: typeof window !== 'undefined' ? window.location.origin + '/' : ''
                    }
                  });
                }
              }
            }
          });
        }
      });
    this.subscriptions.push(this.pracApiSub);
  }

  private handleCompletedClaimLinkError(err: any): boolean {
    const message = (err?.error?.message || err?.message || '').toString();
    if (!this.isCompletedClaimLinkError(message)) {
      return false;
    }

    localStorage.removeItem('claim_number');
    this.claimNumber = '';

    if (this.route.snapshot.queryParams['claim_number']) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { claim_number: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }

    if (this.claimLinkDialogOpen || this.claimLinkHandled) {
      return true;
    }

    this.claimLinkDialogOpen = true;
    this.claimLinkHandled = true;

    const options = new ConfirmPopData();
    options.okButtonText = 'Ok';
    options.okClassName = 'btn-warning';
    options.title = 'Booking Link Used';
    options.message = message || 'This booking has already been completed. You cannot use this link again.';

    this.shared
      .confirmPop(options)
      .afterClosed()
      .subscribe(() => {
        this.claimLinkDialogOpen = false;
      });

    return true;
  }

  private isCompletedClaimLinkError(message: string): boolean {
    const normalizedMessage = message.toLowerCase();

    return (
      !!this.route.snapshot.queryParams['claim_number'] &&
      (normalizedMessage.includes('booking has already been completed') ||
        normalizedMessage.includes('cannot use this link again') ||
        normalizedMessage.includes('already made a booking') ||
        normalizedMessage.includes('link already used'))
    );
  }

  setPracsAfterResponse(params: SearchRequest, get_all = false, filters = false, response: any) {
    if (filters) {
      this.practitionerList = [];
    }
    this.practitionerList = [...this.practitionerList, ...response.data];
    this.isPractDistanceOver20km = this.checkIfDistanceOver20km(this.practitionerList);
    this.totalPages = response.total_pages;
    this.totalItems = response.total_practitioners;
    this.fetchingData = false;
    this.professionTitle = response.professional_title || 'practitioners';
    if (get_all) {
      if (this.pageNumber < this.totalPages) {
        this.pageNumber += 1;
        this.fetchPracList(params, true, filters);
      } else {
        this.fetchingmoreData = false;
      }
    }
    this.generatePracMapCoordList(params);
    this.requestMapRefit();
    this.nodata = this.practitionerList.length === 0;
    if (isPlatformBrowser(this.platformId) && !get_all) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  checkIfDistanceOver20km(practitioners: any[]): boolean {
    if (
      Array.isArray(practitioners) &&
      practitioners.length > 0 &&
      practitioners[0]?.business?.business_location?.distance !== null
    ) {
      const distance = parseFloat(practitioners[0].business.business_location.distance);
      return !isNaN(distance) && distance > 20;
    }
    return false;
  }

  mapBizList: any[];

  generatePracMapCoordList(params: SearchRequest) {
    const mapList: any = [];
    for (let i = 0; i < this.practitionerList.length; i++) {
      const prac = this.practitionerList[i];
      const index = mapList.findIndex((x: any) => x.business.uuid === prac.business.uuid);
      if (index === -1) {
        mapList.push({
          business: prac.business,
          coords: { lat: prac.business.business_location.lat, lng: prac.business.business_location.lng },
          pracs: [prac]
        });
      } else {
        mapList[index].pracs.push(prac);
      }
    }
    this.centerCoords = [
      params?.locObj?.Loclng || this.store.meraLocation.lng,
      params?.locObj?.Loclat || this.store.meraLocation.lat
    ];
    if (!this.centerCoords[0] || !this.centerCoords[1]) {
      this.getCoordinates(this.store.meraLocation);
    } else {
      this.loadingLocations = false;
    }
    this.mapBizList = mapList;
    this.cd.detectChanges();
  }

  toggleBookingType(tab: 'In Person' | 'Online') {
    this.pageNumber = 1;
    this.currentTab = tab;
    this.ensureSortForBookingType();
    this.tabSetManually = true;
    this.totalItems = 0;
    this.practitionerList = [];
  }
  onBookingTypeClick(tab: 'In Person' | 'Online') {
    const tabChanged = this.currentTab !== tab;
    this.toggleBookingType(tab);
    if (tabChanged) {
      this.pageNumber = 1;
      this.fetchListData(false, true);
    }
  }

  fetchRemainingPractitioners() {
    this.pageNumber += 1;
    this.fetchingmoreData = true;
    this.fetchListData(true, false);
  }

  private ensureSortForBookingType() {
    if (this.currentTab === 'Online' && this.orderbyApplied === 'distance') {
      this.orderbyApplied = 'recommended';
    }
  }

  sortdata = [
    { name: 'Closest to me', value: 'distance' },
    { name: 'Earliest availability', value: 'earliest_availability' },
    { name: 'Recommended', value: 'recommended' }
  ];

  get sortList() {
    if (this.currentTab === 'Online') {
      return this.sortdata.filter((item) => item.value !== 'distance');
    }
    return this.sortdata;
  }

  filterdata = [...Constants.FILTERSLIST];
  earliestAvailList = [...Constants.earliestAvailabilityOptions];
  distanceList = [...Constants.distanceOptions];
  appointmentList = [...Constants.appointmentTypes];

  filtersApplied: any = {};
  orderbyApplied = '';
  filterParams = '';

  openFilters() {
    this.availabilityMenuTrigger?.closeMenu();
    this.sortTrigger?.closeMenu();
    this.distanceMenuTrigger?.closeMenu();
    const prevTab = this.currentTab;
    if (Array.isArray(this.filterdata)) {
      this.filterdata.forEach((item) => {
        if (item?.function === 'languages_spoken') {
          item.options = this.languages;
        }
      });
    }
    const dialogData: MatDialogConfig = {
      disableClose: false,
      hasBackdrop: true,
      width: '100%',
      maxWidth: this.isMobile ? '100%' : '600px',
      height: '100%',
      maxHeight: this.isMobile ? '100%' : '600px',
      panelClass: ['filtersPanel', 'popup-container'],
      backdropClass: 'filtersBackdropClass',
      data: {
        filters: this.filterdata,
        tab: this.currentTab,
        service: this.service,
        isPractDistanceOver20km: this.isPractDistanceOver20km
      }
    };

    const dialogRef = this.dialog.open(FiltersComponent, dialogData);
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      const nextTab = result?.currentTab || prevTab;
      const tabChanged = nextTab !== prevTab;

      this.filterdata = Array.isArray(result.filters) ? result.filters : [];
      if (tabChanged) {
        this.toggleBookingType(nextTab);
      } else {
        this.currentTab = nextTab;
      }

      this.filtersApplied = {};
      this.filterParams = '';
      for (let i = 0; i < this.filterdata.length; i++) {
        const filter = this.filterdata[i];
        if (filter.value.length > 0) {
          this.filtersApplied[filter.function] =
            typeof filter.value === 'string' ? filter.value : filter.value.join(',');
        }
      }
      const queryParamsUnchanged =
        JSON.stringify(this.filtersApplied) === JSON.stringify(this.route.snapshot.queryParams);

      this.callQueryParams();

      if (tabChanged || !queryParamsUnchanged) {
        this.pageNumber = 1;
        this.fetchListData(false, true);
      }
    });
  }

  sortBy(option: string) {
    this.orderbyApplied = option;
    this.pageNumber = 1;
    this.fetchListData(false, true);
  }
  filterAvailability(option: []) {
    this.filtersApplied = { ...this.filtersApplied, earliest_availability: option.join(',') };
    Object.keys(this.filtersApplied).forEach((key) => {
      if (!this.filtersApplied[key]) {
        delete this.filtersApplied[key];
      }
    });
    this.pageNumber = 1;
    this.callQueryParams();
  }
  filterDistance(option: string) {
    this.filtersApplied = { ...this.filtersApplied, distance_from_me: option };
    this.pageNumber = 1;
    this.callQueryParams();
  }
  filterAppointmentType(option: []) {
    this.filtersApplied = { ...this.filtersApplied, appointment_type: option.join(',') };
    Object.keys(this.filtersApplied).forEach((key) => {
      if (!this.filtersApplied[key]) {
        delete this.filtersApplied[key];
      }
    });
    this.pageNumber = 1;
    this.callQueryParams();
  }

  callQueryParams() {
    let qParams;
    this.claimNumber = localStorage.getItem('claim_number');
    if (this.orderbyApplied) {
      qParams = this.orderbyApplied;
    }
    if (this.filtersApplied) {
      qParams = this.filtersApplied;
    }
    if (this.claimNumber) {
      qParams.claim_number = this.claimNumber;
    }
    const url = this.router.url.split('?')[0];
    this.router.navigate([url], {
      queryParams: qParams
    });
  }

  setMajorCityAndSuburbInfo() {
    if (
      this.suburb &&
      (this.suburb.toLowerCase() === 'brisbane' ||
        this.suburb.toLowerCase() === 'sydney' ||
        this.suburb.toLowerCase() === 'perth' ||
        this.suburb.toLowerCase() === 'melbourne' ||
        this.suburb.toLowerCase() === 'adelaide')
    ) {
      this.isCitySuburb = true;
    }
  }

  generateLanguageOptions(data: any) {
    const languages = data;
    const transformedArray = languages.map((item: string) => {
      const lowercaseName = item.toLowerCase();
      const formattedName = lowercaseName.replace(/\b\w/g, (firstChar: string) => firstChar.toUpperCase());
      const formattedValue = lowercaseName.replace(/\s+/g, '-');
      return { name: formattedName, value: formattedValue };
    });

    return transformedArray;
  }
  sortOption: string;

  openSortDialog() {
    const position: DialogPosition = { bottom: '0%' };
    const sortConfig: MatDialogConfig = {
      height: 'auto',
      width: 'auto',
      position: position,
      panelClass: ['filtersPanel', 'popup-container'],
      data: { list: this.sortList, selected: this.orderbyApplied }
    };
    const dialogRef = this.dialog.open(SortComponent, sortConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.sortBy(result);
      }
    });
  }

  openMobilePop() {
    const dialogData: MatDialogConfig = {
      disableClose: false,
      hasBackdrop: true,
      width: '100%',
      maxWidth: '400px',
      height: '100%',
      maxHeight: '400px',
      panelClass: ['searchboxPanel', 'popup-container'],
      data: {
        service: this.service,
        location: this.slugText.transform(this.suburb, 'capitalize'),
        date: this.selectedDate
      }
    };
    this.dialog.open(SearchBoxPopComponent, dialogData);
  }

  hiddenCount = 0;
  reduceTotalCount() {
    this.hiddenCount += 1;
  }

  updateCoordinates(e: any) {
    const lat_lon = e.lat + ', ' + e.lng;
    this.api.getGeoCodingByAddress(lat_lon).subscribe({
      next: (response) => {
        let post_code = '3000';
        let city_name = 'Melbourne';
        const list_with_post_code = response.geocode_details.results.find(
          (geocode: any) => geocode.address_components.findIndex((x: any) => x.types.indexOf('postal_code') > -1) > -1
        );

        let addresscomponents = [];
        if (list_with_post_code && list_with_post_code.address_components) {
          addresscomponents = [...list_with_post_code.address_components];
        } else {
          addresscomponents = [...response.geocode_details.results[0].address_components];
        }

        for (let i = 0; i < addresscomponents.length; i++) {
          const component = addresscomponents[i];
          if (component.types.indexOf('postal_code') > -1) {
            post_code = component.long_name;
          } else if (
            component.types.indexOf('locality') > -1 ||
            component.types.indexOf('sublocality') > -1 ||
            component.types.indexOf('neighborhood') > -1
          ) {
            city_name = component.long_name;
          }
        }
        const searchRequest = new SearchRequest();
        searchRequest.cal_date = this.selectedDate;
        searchRequest.service = this.service === 'health-and-wellbeing-services' ? 'all' : this.service;
        searchRequest.symptom = this.symptom;
        searchRequest.online = this.routeType === 'online' ? true : false;
        searchRequest.locObj = new Location();
        searchRequest.locObj.city = city_name;
        searchRequest.locObj.post_code = post_code;
        this.postCode = post_code;
        this.suburb = city_name;
        searchRequest.locObj.state = this.getStateForSuburbByPostCode(post_code);
        searchRequest.locObj.Loclat = e.lat;
        searchRequest.locObj.Loclng = e.lng;
        searchRequest.page_size = this.pageSize;
        searchRequest.distance_range = e.radius;
        this.practitionerList = [];
        this.fetchListData(false, false, searchRequest);
      }
    });
  }

  // --- NEW: map fit state
  mapBounds: [[number, number], [number, number]] | null = null;
  fitBoundsNonce = 0;

  // --- NEW: compute bbox from current map results
  private computeBoundsFromBizList(): [[number, number], [number, number]] | null {
    if (!Array.isArray(this.mapBizList) || this.mapBizList.length === 0) return null;

    let minLng = Number.POSITIVE_INFINITY;
    let minLat = Number.POSITIVE_INFINITY;
    let maxLng = Number.NEGATIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;

    for (const item of this.mapBizList) {
      const lng = Number(item?.coords?.lng);
      const lat = Number(item?.coords?.lat);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    }

    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;

    // identical single pin -> widen slightly so fitBounds won’t over-zoom
    if (minLng === maxLng && minLat === maxLat) {
      const pad = 0.002; // ~200m
      minLng -= pad;
      maxLng += pad;
      minLat -= pad;
      maxLat += pad;
    }

    return [
      [minLng, minLat],
      [maxLng, maxLat]
    ];
  }

  // --- NEW: request a refit (and force change detection via a nonce)
  private requestMapRefit(): void {
    this.mapBounds = this.computeBoundsFromBizList();
    this.fitBoundsNonce++;
  }

  hoverredBusiness: any;

  onMouseEnter(item: any, view: 'provider' | 'practitioner') {
    this.hoverredBusiness = view === 'provider' ? item.uuid : item.business.uuid;
  }

  onMouseOut(item: any, view: 'provider' | 'practitioner') {
    if (view === 'practitioner' && item.business.uuid === this.hoverredBusiness) {
      this.hoverredBusiness = undefined;
    } else if (view === 'provider' && item.uuid === this.hoverredBusiness) {
      this.hoverredBusiness = undefined;
    }
  }

  setQuickFilters(filter: any) {
    this.filtersApplied = filter;
    this.callQueryParams();
  }

  openMenu: string | null = null;
  shouldIgnoreNextClick = false;

  openAvailabilityMenu() {
    if (this.openMenu !== 'earliest_availability') {
      this.distanceMenuTrigger?.closeMenu();
      this.sortTrigger?.closeMenu();
      // this.appointmentTypeTrigger.closeMenu();
    }
    this.openMenu = 'earliest_availability';
    this.shouldIgnoreNextClick = true;
    setTimeout(() => (this.shouldIgnoreNextClick = false), 0);
  }

  openDistanceMenu() {
    if (this.openMenu !== 'distance') {
      this.availabilityMenuTrigger?.closeMenu();
      this.sortTrigger?.closeMenu();
      // this.appointmentTypeTrigger.closeMenu();
    }
    this.openMenu = 'distance';
    this.shouldIgnoreNextClick = true;
    setTimeout(() => (this.shouldIgnoreNextClick = false), 0);
  }

  openAppointmentTypeMenu() {
    if (this.openMenu !== 'appointment_type') {
      this.availabilityMenuTrigger?.closeMenu();
      this.sortTrigger?.closeMenu();
      this.distanceMenuTrigger?.closeMenu();
    }
    this.openMenu = 'appointment_type';
    this.shouldIgnoreNextClick = true;
    setTimeout(() => (this.shouldIgnoreNextClick = false), 0);
  }

  openSortingMenu() {
    this.openMenu = 'sort';
    this.shouldIgnoreNextClick = true;
    setTimeout(() => (this.shouldIgnoreNextClick = false), 0);
  }

  quickFilterItemSelect(item: any, key: string) {
    const obj = { ...this.filtersApplied };
    obj[key] = item.value;

    this.filtersApplied = { ...obj };
    this.callQueryParams();
  }

  clearSelection(key: string) {
    const obj = { ...this.filtersApplied };
    delete obj[key];

    this.filtersApplied = { ...obj };
    this.callQueryParams();
  }

  getSelectedAvailability() {
    const value = this.filtersApplied?.earliest_availability;
    if (value) {
      return this.earliestAvailList.find((x) => x.value === value)?.name || '';
    }
    return '';
  }

  getSelectedDistance() {
    const value = this.filtersApplied?.distance_from_me;
    if (value) {
      return this.distanceList.find((x) => x.value === value)?.name || '';
    }
    return '';
  }

  getSelectedAppointmentType() {
    const value = this.filtersApplied?.appointment_type;
    if (value) {
      return this.appointmentList.find((x) => x.value === value)?.name || '';
    }
    return '';
  }

  getSortByName() {
    return this.sortList.find((x) => x.value === this.orderbyApplied)?.name || '';
  }

  goToBusiness(uuid: string) {
    if (isPlatformBrowser(this.platformId)) {
      const element = document.getElementsByClassName('biz-' + uuid);
      if (element?.length > 0) {
        const elementPosition = element[0].getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - 200;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth' // Optional: Add smooth scrolling
        });
      }
    }
  }

  @HostListener('document:click', ['$event'])
  public onClick(event: MouseEvent) {
    if (!this.openMenu) {
      return;
    }

    if (this.shouldIgnoreNextClick) {
      return; // Skip handling the first click after opening the menu
    }

    const targetElement = event?.target;
    if (!(targetElement instanceof Node)) {
      return;
    }
    const isInsideOpenDropdown = this.isClickInsideOpenDropdown(targetElement);

    if (!isInsideOpenDropdown) {
      this.closeActiveMenu();
    }
  }

  private isClickInsideOpenDropdown(target: Node): boolean {
    const clickedElement = target instanceof Element ? target : target.parentElement;
    if (clickedElement?.closest('.filter-menu, .mat-mdc-menu-panel, .mat-menu-panel')) {
      return true;
    }

    if (this.openMenu === 'earliest_availability') {
      return this.availabilityFilterBox?.nativeElement?.contains(target) || false;
    }
    if (this.openMenu === 'distance') {
      return this.distanceFilterBox?.nativeElement?.contains(target) || false;
    }
    if (this.openMenu === 'sort') {
      return this.sortFilterBox?.nativeElement?.contains(target) || false;
    }
    return false;
  }

  private closeActiveMenu(): void {
    if (this.openMenu === 'earliest_availability') {
      this.availabilityMenuTrigger?.closeMenu();
    } else if (this.openMenu === 'distance') {
      this.distanceMenuTrigger?.closeMenu();
    } else if (this.openMenu === 'sort') {
      this.sortTrigger?.closeMenu();
    }
    this.openMenu = null;
  }

  getProgressPercent(): number {
    return ((this.currentStep + 1) / (this.maxStep + 1)) * 100;
  }

  selectGender(value: string) {
    this.genderCtrl?.setValue(value);
  }

  onNext() {
    if (this.currentStep < this.maxStep) {
      this.currentStep++;
      if (this.currentStep === 1) {
        this.sliderInitialized = false;
        setTimeout(() => this.setSliderProgress(this.distanceCtrl?.value ?? 7));
      }
    }
  }

  onBack() {
    if (this.currentStep > 0) {
      this.currentStep--;
      if (this.currentStep === 1) {
        this.sliderInitialized = false;
        setTimeout(() => this.setSliderProgress(this.distanceCtrl?.value ?? 7));
      }
    }
  }

  onSkip() {
    switch (this.currentStep) {
      case 0:
        this.genderCtrl?.setValue('');
        break;
      case 1:
        this.distanceCtrl?.setValue(7);
        break;
      case 2:
        this.timingsCtrl?.setValue('');
        this.amenitiesCtrl?.setValue('');
        break;
    }

    if (this.currentStep < this.maxStep) {
      this.currentStep++;
      if (this.currentStep === 1) {
        this.sliderInitialized = false;
        setTimeout(() => this.setSliderProgress(this.distanceCtrl?.value ?? 7));
      }
    } else {
      this.refinePreferences();
    }
  }

  onTimingChange(value: string) {
    let selected: string[] = (this.timingsCtrl?.value || '').split(',');
    selected = selected.filter((value) => value);
    const index = selected.indexOf(value);
    if (index > -1) {
      selected.splice(index, 1); // remove if already selected
    } else {
      selected.push(value); // add if not selected
    }

    this.timingsCtrl?.setValue(selected.join(','));
  }

  checkTimeEnabled(time: string) {
    const times: any = this.timingsCtrl?.value;
    return times.indexOf(time) > -1;
  }

  removeEmptyStringValues(obj: any = {}) {
    return Object.fromEntries(
      Object.entries(obj).filter(([key, value]) => key && (typeof value !== 'string' || value.trim() !== ''))
    );
  }
  refinePreferences() {
    this.filtersApplied = {
      ...this.filtersApplied,
      ...this.preferenceForm.getRawValue()
    };

    this.filtersApplied = this.removeEmptyStringValues(this.filtersApplied);

    let url = `/search/${this.service}`;

    if (this.suburb) {
      url += `/${this.suburb}`;
    }
    if (this.postCode) {
      url += `/${this.postCode}`;
    }

    this.router.navigate([url], {
      queryParams: this.filtersApplied
    });
    this.expanded[1] = false;
  }
  get genderCtrl() {
    return this.preferenceForm.get('gender');
  }
  get distanceCtrl() {
    return this.preferenceForm.get('distance_from_me');
  }
  get timingsCtrl() {
    return this.preferenceForm.get('timings');
  }
  get amenitiesCtrl() {
    return this.preferenceForm.get('amenities');
  }
  checkAmenitiesEnabled(value: string) {
    const values = this.amenitiesCtrl?.value;
    return (values || '').indexOf(value) > -1;
  }
  onAmenitiesChange(value: string) {
    const selected: string[] = (this.amenitiesCtrl?.value || '').split(',').filter((v: any) => v);
    const index = selected.indexOf(value);
    if (index > -1) {
      selected.splice(index, 1); // remove if already selected
    } else {
      selected.push(value); // add if not selected
    }
    this.amenitiesCtrl?.setValue(selected.join(','));
  }
}
